<script>
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper.js';
import { emitter } from 'shared/helpers/mitt';

export default {
  name: 'ThemeToggleSwitch',
  data() {
    return {
      currentTheme: 'light',
    };
  },
  mounted() {
    this.currentTheme = this.getCurrentTheme();
    
    // Lắng nghe event từ ThemeSetting
    emitter.on('theme-changed', this.handleThemeChanged);
  },
  beforeUnmount() {
    // Cleanup event listener
    emitter.off('theme-changed', this.handleThemeChanged);
  },
  methods: {
    getCurrentTheme() {
      const savedTheme = LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME);
      if (savedTheme) {
        return savedTheme;
      }
      // Auto detect system theme
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    },
    toggleTheme() {
      const newTheme = this.currentTheme === 'light' ? 'dark' : 'light';
      this.setTheme(newTheme);
    },
    setTheme(theme) {
      this.currentTheme = theme;
      LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, theme);
      
      if (theme === 'auto') {
        const isOSOnDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setColorTheme(isOSOnDarkMode);
      } else {
        setColorTheme(theme === 'dark');
      }
      
      // Emit event để đồng bộ với ThemeSetting
      emitter.emit('theme-changed', theme);
    },
    handleThemeChanged(theme) {
      // Cập nhật state khi nhận event từ ThemeSetting
      this.currentTheme = theme;
    },
  },
};
</script>

<template>
  <div class="theme-toggle-switch">
    <button
      class="switch-button"
      :class="{ 'dark-mode': currentTheme === 'dark' }"
      @click="toggleTheme"
    >
      <div class="switch-track">
        <div class="switch-thumb" :class="{ 'dark': currentTheme === 'dark' }">
          <!-- Sun Icon -->
          <svg
            v-if="currentTheme === 'light'"
            class="icon sun-icon"
            width="8"
            height="8"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="2"/>
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" stroke="currentColor" stroke-width="2"/>
          </svg>
          
          <!-- Moon Icon -->
          <svg
            v-else
            class="icon moon-icon"
            width="8"
            height="8"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" stroke-width="2" fill="currentColor"/>
          </svg>
        </div>
      </div>
    </button>
  </div>
</template>

<style lang="scss" scoped>
.theme-toggle-switch {
  display: flex;
  align-items: center;
  padding: 2px;
}

.switch-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  outline: none;
  margin: 1px 2px;
  &:focus {
    outline: none;
  }
}

.switch-track {
  position: relative;
  width: 22px;
  height: 14px;
  background: #e2e8f0;
  border-radius: 7px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #cbd5e0;
  
  .switch-button.dark-mode & {
    background: #4a5568;
    border-color: #2d3748;
  }
}

.switch-thumb {
  position: absolute;
  top: 1px;
  left: 1px;
  width: var(--space-one);
  height: var(--space-one);
  background: white;
  border-radius: 50%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  
  &.dark {
    transform: translate(var(--space-small), var(--space-zero));
    background: #2d3748;
  }
}

.icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 8px;
  height: 8px;
}

.sun-icon {
  color: #f6ad55; /* Yellow color */
}

.moon-icon {
  color: #9f7aea; /* Purple color */
}

/* Hover effects */
.switch-button:hover .switch-track {
  background: #cbd5e0;
  transform: scale(1.02);
  
  .switch-button.dark-mode & {
    background: #5a6578;
  }
}

.switch-button:hover .switch-thumb {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transform: scale(1.05);
  
  &.dark {
    transform: translate(var(--space-small), var(--space-zero)) scale(1.05);
  }
}

/* Active state */
.switch-button:active .switch-thumb {
  transform: scale(0.95);
  transition: all 0.1s cubic-bezier(0.4, 0, 0.2, 1);
  
  &.dark {
    transform: translate(var(--space-small), var(--space-zero)) scale(0.95);
  }
}

/* Icon transitions */
.icon {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 1;
}

.switch-button:hover .icon {
  transform: rotate(15deg) scale(1.1);
}

.switch-button:active .icon {
  transform: rotate(-5deg) scale(0.9);
}
</style>
